import React, { CSSProperties, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'antd';
import { Modal as UiKitModal, Checkbox, DatePicker, InputAmount, Select, notification } from '@gamesb42/ui-kit';

import useActivationOrder from 'hooks/api/useActivationOrder';
import { getFactorCompany } from 'api/company';
import { CompanyKeyEnum } from 'types/CompanyTypes';
import { AOActionCodeEnum, AODialogTypeEnum, ActionResponse, AvailableAction } from 'types/ActivationOrderTypes';
import { convertFileToBase64 } from 'helpers/file';
import DropZoneDocument from 'components/forms/CompanyForm/DropZoneDocument';
import Button from 'components/uiKit/Button';
import getSaveErrorMessage, { SaveErrorType } from 'components/forms/ActivationOrderForm/getSaveErrorMessage';
import getActionLabel from 'components/forms/ActivationOrderForm/getActionLabel';

import styles from './styles.module.scss';

type PropsT = {
  activationOrderId: string;
  currency?: string;
  amount?: string;
  remainingAmount?: string | null;
  // GB42-3504: on the FUTURE card the same Fund action is titled "Create transaction" per the mockup.
  fundDialogVariant?: 'fundAo' | 'createTransaction';
  onActionSuccess: (status?: string) => void;
};

type BankAccountOption = { label: string; value: number };

// GB42-3427 AC-21/AC-22 + MAX lifecycle doc (п.6): lifecycle actions for CLASSIC/MAX and FUTURE AO
// come only from GET .../actions and go through POST .../actions/{code}. Per the mockups Sign/Fund are
// primary buttons here; Delete/Reject/Archive live in the top-right three-dot menu (see General/ActionsButton).
const BUTTON_ACTION_CODES: string[] = [AOActionCodeEnum.SIGN, AOActionCodeEnum.FUND];

const SIGN_BUTTON_STYLE = { '--bg-color': '#9197AD', width: 101 } as CSSProperties;
const FUND_BUTTON_STYLE = { '--bg-color': '#9197AD' } as CSSProperties;

const toAmount = (value?: string | null) =>
  value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) ? Number(value) : undefined;

const AOActionsBar: FC<PropsT> = ({
  activationOrderId,
  currency,
  amount,
  remainingAmount,
  fundDialogVariant = 'fundAo',
  onActionSuccess,
}) => {
  const { t } = useTranslation();
  const {
    activationOrderActionsData,
    getActivationOrderActions,
    getActivationOrderActionsIsFetching,
    signActivationOrder,
    signActivationOrderIsFetching,
    fundActivationOrder,
    fundActivationOrderIsFetching,
  } = useActivationOrder();

  const [isSignOpen, setIsSignOpen] = useState(false);
  const [signFiles, setSignFiles] = useState<File[]>([]);
  const [isFundOpen, setIsFundOpen] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<BankAccountOption[]>([]);
  const [isBankAccountsLoading, setIsBankAccountsLoading] = useState(false);
  const [fundForm] = Form.useForm();

  const isActionPending =
    getActivationOrderActionsIsFetching || signActivationOrderIsFetching || fundActivationOrderIsFetching;
  const fundTitle =
    fundDialogVariant === 'createTransaction'
      ? t('forms.activationOrder.createTransaction')
      : t('forms.activationOrder.fundAoTitle');
  // Остаток к финансированию; для AO без remaining_amount падаем на полную сумму заказа
  const maxFundAmount = toAmount(remainingAmount) ?? toAmount(amount);

  useEffect(() => {
    if (activationOrderId) getActivationOrderActions({ id: activationOrderId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activationOrderId]);

  useEffect(() => {
    if (!isFundOpen || bankAccounts.length > 0) return;

    setIsBankAccountsLoading(true);
    getFactorCompany()
      .then((company) => {
        const options = (company?.[CompanyKeyEnum.BANK_DETAILS] || [])
          .filter((account) => !currency || account[CompanyKeyEnum.CURRENCY] === currency)
          .map((account) => ({
            label: `${account[CompanyKeyEnum.BANK_NAME]} *${account[CompanyKeyEnum.ACCOUNT]?.slice(-4)}`,
            value: Number(account[CompanyKeyEnum.ID]),
          }));

        setBankAccounts(options);
      })
      .catch(() =>
        notification.error({
          message: t('forms.activationOrder.loadBankAccountsError'),
          theme: 'dark',
          placement: 'bottomLeft',
        }),
      )
      .finally(() => setIsBankAccountsLoading(false));
  }, [isFundOpen, bankAccounts.length, currency, t]);

  useEffect(() => {
    if (isFundOpen) fundForm.setFieldsValue({ amount: maxFundAmount ?? null });
  }, [isFundOpen, maxFundAmount, fundForm]);

  const refreshActions = () => getActivationOrderActions({ id: activationOrderId });

  const handleActionError = (error: SaveErrorType) =>
    notification.error({ message: getSaveErrorMessage(error, t), theme: 'dark', placement: 'bottomLeft' });

  const handleActionSuccess = (label: string) => (result?: ActionResponse) => {
    notification.success({
      message: t('forms.activationOrder.actionSuccess', { action: label }),
      theme: 'dark',
      placement: 'bottomLeft',
    });
    refreshActions();
    onActionSuccess(result?.status);
  };

  const handleFundSuccess = (result?: ActionResponse) => {
    notification.success({
      message: t('forms.activationOrder.fundSuccess'),
      theme: 'dark',
      placement: 'bottomLeft',
    });
    refreshActions();
    onActionSuccess(result?.status);
  };

  // Какой диалог открывать, решает бэкенд через dialog_type
  const handleActionClick = (action: AvailableAction) => {
    if (action.dialog_type === AODialogTypeEnum.UPLOAD_FILE) setIsSignOpen(true);
    if (action.dialog_type === AODialogTypeEnum.FORM) setIsFundOpen(true);
  };

  const closeSignDialog = () => {
    setIsSignOpen(false);
    setSignFiles([]);
  };

  const handleSignSubmit = () => {
    if (signFiles.length === 0) {
      notification.error({
        message: t('forms.activationOrder.signFileRequired'),
        theme: 'dark',
        placement: 'bottomLeft',
      });

      return Promise.resolve();
    }

    return (convertFileToBase64(signFiles[0]) as Promise<string>).then(
      (base64Raw) =>
        new Promise<void>((resolve) => {
          signActivationOrder({
            id: activationOrderId,
            body: {
              file_name: signFiles[0].name,
              file_content: base64Raw.replace(/^data:.*;base64,/, ''),
            },
            successCallback: (result) => {
              handleActionSuccess(t('forms.activationOrder.signAoTitle'))(result);
              closeSignDialog();
              resolve();
            },
            errorCallback: (error: SaveErrorType) => {
              handleActionError(error);
              resolve();
            },
          });
        }),
    );
  };

  const closeFundDialog = () => {
    setIsFundOpen(false);
    fundForm.resetFields();
  };

  const handleFundSubmit = () =>
    fundForm.validateFields().then(
      (values) =>
        new Promise<void>((resolve) => {
          fundActivationOrder({
            id: activationOrderId,
            body: {
              tx_date: values.tx_date.format('YYYY-MM-DD'),
              amount: { currency: currency || 'USD', value: Number(values.amount) },
              factor_bank_account_id: values.factor_bank_account_id,
            },
            successCallback: (result) => {
              handleFundSuccess(result);
              closeFundDialog();
              resolve();
            },
            errorCallback: (error: SaveErrorType) => {
              notification.error({
                message: getSaveErrorMessage(error, t, t('forms.activationOrder.fundError')),
                theme: 'dark',
                placement: 'bottomLeft',
              });
              resolve();
            },
          });
        }),
      () => undefined,
    );

  const buttonActions = (activationOrderActionsData?.available_actions || []).filter((action) =>
    BUTTON_ACTION_CODES.includes(action.code),
  );

  if (buttonActions.length === 0) return null;

  return (
    <>
      <div className={styles.actionsRow}>
        {buttonActions.map((action) => (
          <Button
            key={action.code}
            variant="fill"
            className={styles.actionButton}
            style={action.code === AOActionCodeEnum.SIGN ? SIGN_BUTTON_STYLE : FUND_BUTTON_STYLE}
            disabled={isActionPending}
            onClick={() => handleActionClick(action)}
          >
            {getActionLabel(action, t)}
          </Button>
        ))}
      </div>

      <UiKitModal
        open={isSignOpen}
        title={t('forms.activationOrder.signAoTitle')}
        okText={t('forms.activationOrder.signAoTitle')}
        cancelText={t('forms.activationOrder.cancel')}
        confirmLoading={signActivationOrderIsFetching}
        okButtonProps={{}}
        onOk={handleSignSubmit}
        onCancel={closeSignDialog}
      >
        <DropZoneDocument
          title={t('forms.purchaseRequest.changeStatusModal.signed.titleFile')}
          buttonText={t('forms.purchaseRequest.changeStatusModal.signed.titleButton')}
          setAccessFiles={setSignFiles}
          accessFiles={signFiles}
        />
      </UiKitModal>

      <UiKitModal
        open={isFundOpen}
        width={766}
        title={fundTitle}
        okText={fundTitle}
        cancelText={t('forms.activationOrder.cancel')}
        confirmLoading={fundActivationOrderIsFetching}
        okButtonProps={{}}
        onOk={handleFundSubmit}
        onCancel={closeFundDialog}
      >
        <Form form={fundForm} layout="vertical" className={styles.fundForm}>
          <div className={styles.fundRow}>
            <Form.Item
              name="tx_date"
              rules={[{ required: true, message: t('forms.activationOrder.fundDateRequired') }]}
            >
              <DatePicker label={t('forms.activationOrder.transactionDate')} size="large" format="DD.MM.YYYY" />
            </Form.Item>
            <Form.Item
              name="amount"
              rules={[
                { required: true, message: t('forms.activationOrder.fundAmountRequired') },
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') return Promise.resolve();

                    const numericValue = Number(value);

                    if (!Number.isFinite(numericValue) || numericValue <= 0) {
                      return Promise.reject(new Error(t('forms.activationOrder.fundAmountPositive')));
                    }

                    if (maxFundAmount !== undefined && numericValue > maxFundAmount) {
                      return Promise.reject(
                        new Error(t('forms.activationOrder.fundAmountMax', { max: maxFundAmount })),
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputAmount label={t('forms.activationOrder.amount')} currency={currency as 'USD' | 'EUR'} size="large" />
            </Form.Item>
          </div>
          <Form.Item
            name="factor_bank_account_id"
            rules={[{ required: true, message: t('forms.activationOrder.fundBankAccountRequired') }]}
          >
            <Select
              label={t('factorDetails.factorBankAccount')}
              options={bankAccounts}
              loading={isBankAccountsLoading}
              size="large"
            />
          </Form.Item>
          {/* GB42-3504: recipient always defaults to the AO company, so recipient_company_id is not sent.
              Picking another recipient is out of scope until the API contract for it is agreed. */}
          <Checkbox checked disabled>
            {t('forms.activationOrder.recipientSameAsClient')}
          </Checkbox>
        </Form>
      </UiKitModal>
    </>
  );
};

export default AOActionsBar;
